<template>
  <div class="space-y-16">
    <h3 class="heading-1">{{ i18n(translations.stepTitle) }}</h3>

    <CardGuidance
      clickable
      :icon="VodafoneLogo"
      :label="i18n(translations.cardGuidanceExternalTelcoLabel)"
      :title="i18n(translations.cardGuidanceExternalTelcoTitle)"
      :tracking="{
        zone: 'mobile_plan_pickers',
        name: 'more_info',
      }"
      @click="openMobilePlanModal"
    />

    <MobilePlanOptions
      :options="mobilePlanOptions"
      @select="openMobilePlanModal"
    />
  </div>

  <ClientOnly>
    <MobilePlanDrawer
      :name="MODAL_NAMES.MOBILE_PLAN_FAKE_DOOR"
      selected-mobile-plan="VODAFONE_OFFERS"
      :title="i18n(translations.externalTelcoDrawerTitle)"
    />
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'

import { MODAL_NAMES } from '~/scopes/product/constants'

import CardGuidance from '../../../CardGuidance/CardGuidance.vue'
import MobilePlanDrawer from '../MobilePlanDrawer/MobilePlanDrawer.vue'
import MobilePlanOptions from '../MobilePlanOptions/MobilePlanOptions.vue'

import translations from './ExternalTelcoMobilePlan.translations'
import VodafoneLogo from './components/VodafoneLogo/VodafoneLogo.vue'

const i18n = useI18n()
const {
  market: { countryCode },
} = useMarketplace()
const tracking = useTracking()
const logger = useLogger()

const mobilePlanOptions = computed(() => {
  if (countryCode === 'GB') {
    return [
      {
        key: 'external-telco-no-mobile-plan',
        label: i18n(translations.noMobilePlan),
      },
      {
        key: 'external-telco-mobile-plan-1',
        label: i18n(translations.option27GB),
        subLabel: i18n(translations.cancelAnytime),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('10'),
        }),
      },
      {
        key: 'external-telco-mobile-plan-2',
        label: i18n(translations.unlimitedDataPlan),
        subLabel: i18n(translations.cancelAnytime),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('40'),
        }),
      },
    ]
  }

  if (countryCode === 'DE') {
    return [
      {
        key: 'external-telco-no-mobile-plan',
        label: i18n(translations.noMobilePlan),
      },
      {
        key: 'external-telco-mobile-plan-1',
        label: i18n(translations.option15GB),
        subLabel: i18n(translations.cancelAnytime),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('9.99'),
        }),
      },
      {
        key: 'external-telco-mobile-plan-2',
        label: i18n(translations.option45GB),
        subLabel: i18n(translations.cancelAnytime),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('19.99'),
        }),
      },
    ]
  }

  if (countryCode === 'ES') {
    return [
      {
        key: 'external-telco-no-mobile-plan',
        label: i18n(translations.noMobilePlan),
      },
      {
        key: 'external-telco-mobile-plan-1',
        label: i18n(translations.option25GB),
        subLabel: i18n(translations.contract24Month),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('10'),
        }),
      },
      {
        key: 'external-telco-mobile-plan-2',
        label: i18n(translations.unlimitedDataPlan),
        subLabel: i18n(translations.contract24Month),
        price: i18n(translations.pricePerMonth, {
          pricePerMonth: i18n.price('20'),
        }),
      },
    ]
  }

  logger.error(`[PRODUCT] No mobile plan options found ${countryCode}`, {
    owners: ['bot-squad-b2cservices-front'],
  })

  return []
})

function openMobilePlanModal() {
  tracking.trackModal({
    name: 'VODAFONE_OFFERS',
    status: 'opened',
    zone: 'mobile_plan_pickers',
  })
  openModal(MODAL_NAMES.MOBILE_PLAN_FAKE_DOOR)
}
</script>
